import { css } from '@bts-web/utils-style-engine';
import { AssetImage } from '@bts-web/core-features/common';
import { ReactNode } from 'react';
import { RegularArrowRightGray } from '../../svgIcons';

const descriptionStyles = css({
  color: 'neutrals.text_secondary',
  fontSize: 'caption.small',
  fontWeight: 'caption.small',
  lineHeight: 'caption.small',
  letterSpacing: 'caption.small',
});

const valueStyles = css({
  color: 'neutrals.text_primary',
  fontSize: 'label.medium_medium',
  fontWeight: 'label.medium_medium',
  lineHeight: 'label.medium_medium',
  letterSpacing: 'label.medium_medium',
});

const amountStyles = css({
  fontSize: 'label.medium_medium',
  fontWeight: 'label.medium_medium',
  lineHeight: 'label.medium_medium',
  letterSpacing: 'label.medium_medium',
});

type AssetCellPropsBase = {
  nameDescription: string;
  name: string;
  valueDescription?: string;
  value?: string;
  onClick?: () => void;
  className?: string;
  dataTestId?: string;
};

type AssetCellPropsWithLogoUrl = AssetCellPropsBase & {
  logoUrl: string;
  icon?: never;
};

type AssetCellPropsWithIcon = AssetCellPropsBase & {
  logoUrl?: never;
  icon: ReactNode;
};

type AssetCellProps = AssetCellPropsWithIcon | AssetCellPropsWithLogoUrl;

export const AssetCell = ({
  logoUrl,
  name,
  icon,
  nameDescription,
  value,
  valueDescription,
  onClick,
  className,
  dataTestId = 'asset-cell',
}: AssetCellProps) => {
  return (
    <div
      data-testid={dataTestId}
      className={`${css({
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'medium',
        '&:last-child': {
          borderBottom: 'none',
        },
        cursor: onClick ? 'pointer' : 'default',

        ...(onClick
          ? {
              '&:active': {
                backgroundColor: 'neutrals.fill_focused',
                boxShadow:
                  'var(--spacing-medium) 0 0 0 var(--colors-neutrals-fill_focused), calc(var(--spacing-medium) * -1) 0 0 0 var(--colors-neutrals-fill_focused)',
              },
            }
          : {}),
      })} ${className ?? ''}`}
      onClick={onClick}
    >
      <div
        className={css({
          display: 'flex',
          gap: 'medium',
          alignItems: 'center',
        })}
      >
        {icon ? (
          icon
        ) : (
          <AssetImage
            height={40}
            width={40}
            src={logoUrl}
            alt={name ?? 'Asset logo'}
            placeholder="empty"
          />
        )}

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
            flex: 1,
          })}
        >
          <span className={descriptionStyles}>{nameDescription}</span>

          <span className={valueStyles}>{name}</span>
        </div>
      </div>

      <div
        className={css({ display: 'flex', alignItems: 'center', gap: 'small' })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
            justifyContent: valueDescription ? 'flex-start' : 'center',
            alignItems: 'flex-end',
            textAlign: 'end',
          })}
        >
          {valueDescription && (
            <span className={descriptionStyles}>{valueDescription}</span>
          )}

          {value && (
            <span className={`${valueStyles} ${amountStyles}`}>{value}</span>
          )}
        </div>

        {onClick && (
          <RegularArrowRightGray
            size={20}
            className={css({
              _rtl: {
                transform: 'rotate(180deg)',
              },
            })}
          />
        )}
      </div>
    </div>
  );
};
